import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #f8f8f8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1.5em;
  min-height: 400px;
  gap: 24px;
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const Title = styled.h1`
  font-size: 1.5em;
  color: ${({ colors }) => colors.labelText};
  margin-bottom: 1em;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 0.9em;
  min-width: 640px;

  thead th {
    ${adminTableHeaderCell};
    padding: 0.6em;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 1em;
  }

  .LinkButton {
    color: #fff;
    background-color: ${({ colors }) => colors.buttonBackground};
    cursor: pointer;
    border-radius: 3px;
    font-size: 1em;
    padding: 0.5em;
    margin-left: 0.5em;

    &:hover {
      background-color: ${LINK_HOVER_COLOR};
      text-decoration: none;
    }
  }
`;

export default function StatsUploadContent({ colors }) {
  const subscription = useSelector((s) => s.auth.selected_subscriptions) || {};
  const invoiceId   = subscription.invoice_id;
  const customerId  = subscription.customer_id;

  const [sheets, setSheets]       = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!invoiceId || !customerId) {
      setLoading(false);
      return;
    }

    API.getSTATs({ invoice_id: invoiceId, customer_id: customerId })
      .then((resp) => {
        if (resp.status === "Success" && resp.stats) {
          // Map and sort descending by date
          const arr = Object.values(resp.stats)
            ?.map((s) => ({
              id: s.statsheet_id,
              date: s.inserted,
              status: s.status,
              teachers: s.teachers,
              schools: s.schools,
              uploadedBy: `${s.first_name} ${s.last_name}`,
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          setSheets(arr);
        } else {
          setSheets([]);
        }
      })
      .catch((err) => {
        console.error("Failed to load STAT sheets:", err);
        setSheets([]);
      })
      .finally(() => setLoading(false));
  }, [invoiceId, customerId]);

  return (
    <Container>
      <View>
        <Title colors={colors}>STAT sheets</Title>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <TableScroll>
            <Table colors={colors}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Teachers</th>
                  <th>Schools</th>
                  <th>Uploaded By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sheets.length > 0 ? (
                  sheets.map((s) => (
                    <tr key={s.id}>
                      <td>{s.date}</td>
                      <td>{s.status}</td>
                      <td>{s.teachers}</td>
                      <td>{s.schools}</td>
                      <td>{s.uploadedBy}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "1em" }}>
                      No STAT sheets uploaded.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </View>
    </Container>
  );
}
